import React from "react";
import PageShell from "@/components/PageShell";
import { ShieldCheck, Lock, MapPinOff, Mail, Building2, Wallet } from "lucide-react";

const Pillar = ({ icon: Icon, title, text }) => (
  <div className="sn-glass-dark rounded-2xl p-5 flex gap-4 items-start">
    <div
      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
      style={{
        background: "linear-gradient(140deg, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.06) 100%)",
        border: "1px solid rgba(34,197,133,0.35)",
      }}
    >
      <Icon className="text-[#22C585]" size={20} />
    </div>
    <div>
      <h4
        className="font-semibold text-white"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        {title}
      </h4>
      <p className="text-sm text-[#C7D0DF] mt-1 leading-relaxed">{text}</p>
    </div>
  </div>
);

const Privacy = () => {
  return (
    <PageShell navyTheme={true}>
      <section className="relative">
        <div className="mx-auto max-w-4xl px-4 py-8 md:py-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[#22C585] font-semibold">
            Legal
          </div>
          <h1
            className="mt-2 text-4xl md:text-5xl font-extrabold text-white leading-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
            data-testid="privacy-title"
          >
            SpendNova&trade; Privacy Policy
          </h1>
          <p className="mt-3 text-[#C7D0DF]">
            Last updated:{" "}
            {new Date().toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <Pillar
              icon={ShieldCheck}
              title="Bunker Security"
              text="A strict security model that treats every credential and byte as classified."
            />
            <Pillar
              icon={Wallet}
              title="Financial data minimization"
              text="We use Plaid to view balances — we never store bank logins or sell your data."
            />
            <Pillar
              icon={MapPinOff}
              title="Foreground-only GPS"
              text="Location is used only while the app is open. No background tracking. Ever."
            />
          </div>

          {/* PROMINENT: Client-side data & no background tracking callout */}
          <div
            className="mt-6 rounded-2xl p-5 md:p-6"
            style={{
              background:
                "linear-gradient(135deg, rgba(34,197,133,0.16) 0%, rgba(34,197,133,0.06) 100%)",
              border: "1px solid rgba(34,197,133,0.45)",
              boxShadow:
                "0 0 0 1px rgba(34,197,133,0.10) inset, 0 20px 50px -25px rgba(34,197,133,0.30)",
            }}
            data-testid="privacy-client-side-callout"
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: "rgba(34,197,133,0.22)",
                  border: "1px solid rgba(34,197,133,0.55)",
                }}
              >
                <ShieldCheck size={20} className="text-[#22C585]" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold"
                >
                  Data & Location — Reviewer Note
                </div>
                <p
                  className="mt-2 text-[15px] md:text-base text-white leading-relaxed"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                >
                  <strong>All user inputs for TDY, PCS, BAH, and BAS stay
                  client-side on the device.</strong> Location services are
                  restricted to <strong>on-device installation routing</strong>{" "}
                  only, with <strong>absolutely zero background location
                  tracking</strong> and <strong>zero data monetization</strong>.
                </p>
              </div>
            </div>
          </div>

          <article
            className="sn-glass-strong-dark rounded-3xl p-6 md:p-10 mt-10 sn-prose-dark"
            data-testid="privacy-content"
          >
            <h2>1. Our Commitment to Your Privacy</h2>
            <p>
              SpendNova™ is built to protect the financial health of our users,
              including professionals, families, and military personnel. We
              follow a strict{" "}
              <strong>"Bunker Security"</strong> model to ensure your personal
              and financial information is never misused, sold, or exposed.
            </p>

            <h2>2. Financial Data & Plaid Connectivity</h2>
            <p>
              SpendNova uses <strong>Plaid, Inc. ("Plaid")</strong> to connect
              your financial accounts. By using our service, you grant SpendNova
              and Plaid the right, power, and authority to act on your behalf to
              access and transmit your personal and financial information from
              your relevant financial institution.
            </p>
            <p>
              <strong>Read-only, balance-only access.</strong> SpendNova strictly
              reads <strong>Account Balances</strong> for the purpose of personal
              budgeting, subscription management, and travel planning. We do not
              initiate transactions, move funds, or take any action on your
              accounts.
            </p>
            <ul>
              <li>
                We do <strong>NOT</strong> store your bank login credentials or
                passwords. All authentication happens inside Plaid's secured
                environment.
              </li>
              <li>
                We do <strong>NOT</strong> pull your full transaction history
                unless required for a specific feature (e.g. bill matching),
                and only with your explicit in-app consent.
              </li>
              <li>
                We <strong>NEVER</strong> sell your financial data to third
                parties, advertisers, or data brokers.
              </li>
              <li>
                You can revoke SpendNova's access to your linked institution at
                any time from within the app or via Plaid's Portal at{" "}
                <a
                  href="https://my.plaid.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  my.plaid.com
                </a>
                .
              </li>
            </ul>
            <p>
              <strong>Security standards.</strong> All financial tokens returned
              by Plaid (including access tokens and item identifiers) are stored
              using <strong>AES-256 field-level encryption</strong> at rest, with
              per-record keys managed under our Bunker Security key-hierarchy.
              Data in transit is protected by TLS 1.3. No plaintext token or
              credential ever touches SpendNova disk storage or logs.
            </p>

            <h2>3. Foreground-Only GPS Tracking</h2>
            <p>
              Location tracking in SpendNova operates strictly on an{" "}
              <strong>"Only While Using the App"</strong> basis.
            </p>
            <ul>
              <li>
                Background tracking is <strong>100% disabled</strong> when the app is
                closed or locked.
              </li>
              <li>
                Your coordinates are processed instantly to locate nearby military
                installations and rest stops.
              </li>
              <li>
                Location services are restricted to{" "}
                <strong>on-device installation routing only</strong> — coordinates
                are never uploaded, retained, or sold.
              </li>
              <li>
                We <strong>NEVER</strong> monetize, sell, or share your location
                or personal data with data brokers, advertisers, or any third
                party.
              </li>
            </ul>

            <h2>4. Financial Calculations & Inputs</h2>
            <p>
              All user inputs used to build your budget — including{" "}
              <strong>BAH</strong>, <strong>GS pay</strong>,{" "}
              <strong>business income</strong>, <strong>TDY per-diem</strong>,{" "}
              <strong>PCS</strong>, and <strong>BAS</strong> — are processed and
              stored <strong>client-side on your device</strong>. These values
              are used only to compute your local budget, allowance, and travel
              summaries within the app.
            </p>
            <ul>
              <li>
                Your inputs are <strong>never</strong> transmitted to a
                government system, employer, or third-party financial network.
              </li>
              <li>
                SpendNova™ does <strong>not</strong> handle active banking
                transactions, issue credit, or manage real money deposits.
              </li>
              <li>
                Your inputs remain yours — no monetization, no data resale, no
                background telemetry.
              </li>
            </ul>

            <h2>5. Contact Us</h2>
            <p>
              Questions? Contact us at{" "}
              <a href="mailto:privacy@myspendnova.com">privacy@myspendnova.com</a>.
            </p>

            <div
              className="mt-8 rounded-2xl p-5 flex items-center gap-3"
              style={{
                background:
                  "linear-gradient(120deg, rgba(34,197,133,0.14) 0%, rgba(34,197,133,0.04) 100%)",
                border: "1px solid rgba(34,197,133,0.35)",
              }}
            >
              <Mail className="text-[#22C585]" size={20} />
              <div className="text-sm text-white">
                <span className="font-semibold">privacy@myspendnova.com</span> — we
                respond to every inquiry within 3 business days.
              </div>
            </div>

            <div
              className="mt-5 rounded-2xl p-5 flex items-center gap-3"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <Building2 className="text-[#22C585]" size={20} />
              <div className="text-sm text-white">
                Built with <span className="font-semibold">Bunker Security</span>{" "}
                for those who demand the highest standards of financial
                privacy.
              </div>
            </div>
          </article>
        </div>
      </section>
    </PageShell>
  );
};

export default Privacy;
